import React, { type ReactNode, useCallback } from 'react';

import { ErrorBoundary, type ErrorBoundaryProps } from 'react-error-boundary';

import { useNavBoundaryContext } from '@atlassian/navigation-error-boundary/nav-boundary-context';
import type { NavigationExperienceIdEnum } from '@atlassian/navigation-perf-utils/types';

import { PACKAGE_PREFIX } from '../../constants';
import { useMenuStateContext, useUniversalCreateState } from '../../context';

interface GenericErrorBoundaryProps {
	/**
	 * Optional app name to help identify the app where the error occurred when context is not available.
	 */
	app?: string;
	children: ReactNode;
	fallback?: ErrorBoundaryProps['fallback'];
	/**
	 * The interaction ID to help identify the experience where the error occurred.
	 */
	interactionId?: NavigationExperienceIdEnum;
	/**
	 * The name of the component using this error boundary to help identify where errors occur.
	 */
	name: string;
	onError?: (error: Error) => void;
}

export function GenericErrorBoundary({
	children,
	name,
	interactionId = undefined,
	fallback = null,
	onError = undefined,
	app = undefined,
}: GenericErrorBoundaryProps): React.JSX.Element {
	const { product, subProduct } = useMenuStateContext();
	const { product: productV1, subProduct: subProductV1 } = useUniversalCreateState();
	const tags = {
		component: PACKAGE_PREFIX,
		subProduct: subProduct || subProductV1,
	};

	const { handleError } = useNavBoundaryContext({
		location: name,
		disableSentry: false,
		disableAnalytics: true,
		onError,
		sentryConfig: { product: app || product || productV1 },
		interactionId,
		tags,
	});

	const handleOnError = useCallback(
		async (error: Error) => {
			handleError(error);
		},
		[handleError],
	);

	return (
		<ErrorBoundary fallback={fallback} onError={handleOnError}>
			{children}
		</ErrorBoundary>
	);
}
